import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE } from '../config/api';
import { emitAthenaEvent, ATHENA_EVENT_SOURCES, ATHENA_EVENT_TYPES, subscribeAthenaEvents } from '../events/AthenaEventBus';
import {
  bindAthenaDeviceContextOwner,
  clearAthenaDeviceContextForUser,
  getAthenaDeviceContextCapabilities,
  getAthenaDeviceContextSnapshot,
  getAthenaNativeInstallation,
  getAthenaRuntimeCapabilities,
  isAthenaDeviceContextAvailable,
  normalizeAthenaDeviceContextEvent,
  observeAthenaDeviceContext,
  setAthenaDeviceContextConsent,
  setAthenaDeviceContextEnabledSignals,
  startAthenaDeviceContextObserving,
  stopAthenaDeviceContextObserving,
} from '../platform/AthenaDeviceContext';
import { useAuth } from './AuthContext';
import { useAthenaRemoteConfig } from '../config/RemoteConfigProvider';
import { requestedAthenaDeviceSignalsForMessage } from './deviceContextChat';

const AthenaDeviceContext = createContext(null);
const PREFERENCES_PREFIX = 'athena_device_context_preferences_v1:';

export const ATHENA_DEVICE_CONTEXT_DEFAULTS = Object.freeze({
  // Device context is an explicit feature. Existing Android permissions do not
  // imply consent to collect signals for Athena.
  enabled: false,
  network: false,
  battery: false,
  audio: false,
  appState: false,
});

const RELEVANT_SIGNALS = new Set(['network', 'battery', 'audio', 'appState', 'temporal', 'capabilities']);

function safeOwnerId(value) {
  const id = typeof value === 'string' ? value.trim() : String(value || '').trim();
  return /^[A-Za-z0-9._:-]{1,180}$/.test(id) ? id : '';
}

function preferencesKey(ownerId) {
  const safe = safeOwnerId(ownerId);
  return safe ? `${PREFERENCES_PREFIX}${safe}` : '';
}

function normalizePreferences(value) {
  const raw = value && typeof value === 'object' && !Array.isArray(value) ? value : {};
  const preference = (localKey, remoteKey) => (
    typeof raw[localKey] === 'boolean' ? raw[localKey] : raw[remoteKey] === true
  );
  return Object.freeze({
    enabled: preference('enabled', 'device_context_enabled'),
    network: preference('network', 'network_enabled'),
    battery: preference('battery', 'battery_enabled'),
    audio: preference('audio', 'audio_context_enabled'),
    // App state is local-only: it is never restored from a server preference.
    appState: raw.appState === true || raw.app_state === true,
  });
}

function workerPreferences(preferences) {
  const safe = normalizePreferences(preferences);
  return Object.freeze({
    device_context_enabled: safe.enabled,
    battery_enabled: safe.battery,
    network_enabled: safe.network,
    audio_context_enabled: safe.audio,
    automation_enabled: false,
    notification_context_enabled: false,
  });
}

function readLocalPreferences(ownerId) {
  const key = preferencesKey(ownerId);
  if (!key || typeof localStorage === 'undefined') return ATHENA_DEVICE_CONTEXT_DEFAULTS;
  try { return normalizePreferences(JSON.parse(localStorage.getItem(key) || '{}')); } catch { return ATHENA_DEVICE_CONTEXT_DEFAULTS; }
}

function writeLocalPreferences(ownerId, preferences) {
  const key = preferencesKey(ownerId);
  if (!key || typeof localStorage === 'undefined') return;
  try { localStorage.setItem(key, JSON.stringify(normalizePreferences(preferences))); } catch { /* Storage can be unavailable. */ }
}

function emptySnapshot(capabilities = {}) {
  return Object.freeze({
    platform: 'unknown',
    appState: 'unknown',
    network: Object.freeze({ connected: null, type: 'unknown', metered: null }),
    battery: null,
    charging: null,
    powerSaver: null,
    audioOutput: 'unknown',
    timezone: 'UTC',
    locale: 'pt-BR',
    capabilities: Object.freeze({ ...capabilities }),
    collectedAt: null,
  });
}

function nativeEnabledSignals(preferences) {
  const safe = normalizePreferences(preferences);
  return Object.freeze({
    battery: safe.enabled && safe.battery,
    network: safe.enabled && safe.network,
    audio: safe.enabled && safe.audio,
    appState: safe.enabled && safe.appState,
  });
}

function hasEnabledSignal(preferences) {
  const signals = nativeEnabledSignals(preferences);
  return signals.battery || signals.network || signals.audio || signals.appState;
}

function hasRequestedEnabledSignal(preferences, requestedSignals) {
  const safe = normalizePreferences(preferences);
  const signals = allowedSignals(requestedSignals);
  return (signals.includes('network') && safe.network)
    || (signals.includes('battery') && safe.battery)
    || (signals.includes('audio') && safe.audio)
    || (signals.includes('appState') && safe.appState);
}

/**
 * A defensive second boundary for pre-F5 APKs: fields disabled in the
 * settings never remain in React state or leave the WebView, even if an old
 * native bridge cannot yet suppress them at collection time.
 */
function maskSnapshotForPreferences(snapshot, preferences, capabilities = {}) {
  const source = snapshot && typeof snapshot === 'object' ? snapshot : emptySnapshot(capabilities);
  const safe = normalizePreferences(preferences);
  const signals = nativeEnabledSignals(safe);
  const anySignalEnabled = signals.battery || signals.network || signals.audio || signals.appState;
  return Object.freeze({
    ...source,
    appState: signals.appState ? source.appState : 'unknown',
    network: signals.network
      ? source.network
      : Object.freeze({ connected: null, type: 'unknown', metered: null }),
    battery: signals.battery ? source.battery : null,
    charging: signals.battery ? source.charging : null,
    powerSaver: signals.battery ? source.powerSaver : null,
    audioOutput: signals.audio ? source.audioOutput : 'unknown',
    collectedAt: anySignalEnabled ? source.collectedAt : null,
  });
}

function allowedSignals(input) {
  const values = Array.isArray(input) ? input : [];
  return [...new Set(values.filter((value) => RELEVANT_SIGNALS.has(value)))];
}

function safeSnapshotEnum(value, allowed, fallback = null) {
  const normalized = typeof value === 'string' ? value.trim().toLowerCase() : '';
  return allowed.has(normalized) ? normalized : fallback;
}

function boundedPercent(value) {
  const number = Number(value);
  return Number.isInteger(number) && number >= 0 && number <= 100 ? number : null;
}

/** Maps only the Worker allowlist; no raw device snapshot is sent. */
function workerSnapshot(context) {
  const source = context && typeof context === 'object' ? context : {};
  const snapshot = {};
  const appState = safeSnapshotEnum(source.appState, new Set(['foreground', 'background', 'unknown']));
  if (appState) snapshot.app_state = appState;

  if (source.network && typeof source.network === 'object') {
    if (typeof source.network.connected === 'boolean') snapshot.network_state = source.network.connected ? 'online' : 'offline';
    const rawType = safeSnapshotEnum(source.network.type, new Set(['wifi', 'cellular', 'ethernet', 'other', 'unknown', 'none', 'bluetooth']));
    if (rawType) snapshot.network_type = rawType === 'none' ? 'unknown' : rawType === 'bluetooth' ? 'other' : rawType;
    if (typeof source.network.metered === 'boolean') snapshot.network_metered = source.network.metered;
  }

  const battery = boundedPercent(source.battery);
  if (battery !== null) snapshot.battery_percent = battery;
  if (typeof source.charging === 'boolean') snapshot.charging = source.charging;
  if (typeof source.powerSaver === 'boolean') snapshot.power_saver = source.powerSaver;
  const audio = safeSnapshotEnum(source.audioOutput, new Set(['wired', 'bluetooth', 'speaker', 'none', 'unknown']));
  if (audio) snapshot.audio_output = audio;
  if (typeof source.timezone === 'string' && /^[A-Za-z0-9_+\-/]{1,80}$/.test(source.timezone)) snapshot.timezone = source.timezone;
  if (typeof source.locale === 'string' && /^[A-Za-z0-9-]{2,35}$/.test(source.locale)) snapshot.locale = source.locale;
  return Object.freeze(snapshot);
}

function contextIdempotencyKey(snapshot, collectedAt) {
  // Deterministic for the same reduced snapshot; contains no account, device,
  // location, message or notification data.
  const timestamp = Date.parse(collectedAt || '');
  // Coarsely rotate the key so a later valid snapshot cannot collide with a
  // retained row from yesterday, while still suppressing burst duplicates.
  const bucket = Math.floor((Number.isFinite(timestamp) ? timestamp : Date.now()) / (5 * 60 * 1000));
  const value = `${JSON.stringify(snapshot)}:${bucket}`;
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return `ctx-${(hash >>> 0).toString(36)}`;
}

/**
 * Reduces a raw snapshot to only signals relevant to the current explicit
 * request. Nothing calls this automatically from the chat composer.
 */
export function selectRelevantAthenaDeviceContext(snapshot, preferences, requestedSignals = []) {
  const safePreferences = normalizePreferences(preferences);
  if (!safePreferences.enabled || !snapshot) return Object.freeze({});
  const signals = allowedSignals(requestedSignals);
  if (!signals.length) return Object.freeze({});

  const result = {};
  if (signals.includes('temporal')) {
    result.timezone = snapshot.timezone;
    result.locale = snapshot.locale;
  }
  if (signals.includes('capabilities')) result.capabilities = snapshot.capabilities;
  if (signals.includes('appState') && safePreferences.appState) result.appState = snapshot.appState;
  if (signals.includes('network') && safePreferences.network) result.network = snapshot.network;
  if (signals.includes('battery') && safePreferences.battery) {
    result.battery = snapshot.battery;
    result.charging = snapshot.charging;
    result.powerSaver = snapshot.powerSaver;
  }
  if (signals.includes('audio') && safePreferences.audio) result.audioOutput = snapshot.audioOutput;
  // Do not publish a placeholder merely because the feature is enabled. A
  // category must both be explicitly requested and explicitly allowed.
  if (!Object.keys(result).length) return Object.freeze({});
  return Object.freeze({ ...result, collectedAt: snapshot.collectedAt, platform: snapshot.platform });
}

async function authenticatedFetch(path, token, options = {}) {
  if (!token) return null;
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: { Authorization: `Bearer ${token}`, ...(options.headers || {}) },
    });
    if (!response.ok) return null;
    return response.status === 204 ? {} : response.json();
  } catch {
    return null;
  }
}

/** Called from AuthContext on logout, even if the Provider has unmounted. */
export async function clearAthenaDeviceContextRuntime(ownerId = '') {
  const key = preferencesKey(ownerId);
  if (key && typeof localStorage !== 'undefined') {
    try { localStorage.removeItem(key); } catch { /* noop */ }
  }
  // Stop observation before clearing an owner binding. Running those native
  // calls concurrently can let a late callback recreate state for the account
  // that is leaving the app.
  try { await stopAthenaDeviceContextObserving(); } catch { /* noop */ }
  try { await clearAthenaDeviceContextForUser(ownerId); } catch { /* noop */ }
}

export function AthenaDeviceContextProvider({ children }) {
  const { user } = useAuth();
  const remoteConfig = useAthenaRemoteConfig();
  const deviceContextReleased = remoteConfig.featureEnabled('deviceContext');
  const ownerId = safeOwnerId(user?.id);
  const [preferences, setPreferencesState] = useState(ATHENA_DEVICE_CONTEXT_DEFAULTS);
  const [snapshot, setSnapshot] = useState(() => emptySnapshot());
  const [capabilities, setCapabilities] = useState(() => emptySnapshot().capabilities);
  const [runtimeCapabilities, setRuntimeCapabilities] = useState(null);
  const preferencesRef = useRef(preferences);
  const ownerRef = useRef(ownerId);

  useEffect(() => { preferencesRef.current = preferences; }, [preferences]);
  useEffect(() => { ownerRef.current = ownerId; }, [ownerId]);

  const refreshSnapshot = useCallback(async () => {
    if (!isAthenaDeviceContextAvailable() || !preferencesRef.current.enabled || !hasEnabledSignal(preferencesRef.current) || !deviceContextReleased) return emptySnapshot(capabilities);
    const rawSnapshot = await getAthenaDeviceContextSnapshot();
    const next = maskSnapshotForPreferences(rawSnapshot, preferencesRef.current, capabilities);
    setSnapshot(next);
    emitAthenaEvent({
      type: ATHENA_EVENT_TYPES.DEVICE_CONTEXT_CHANGED,
      source: ATHENA_EVENT_SOURCES.DEVICE,
      metadata: { signal: 'snapshot', available: isAthenaDeviceContextAvailable() },
      idempotencyKey: `device-context:snapshot:${next.collectedAt || 'unknown'}`,
    });
    return next;
  }, [capabilities, deviceContextReleased]);

  useEffect(() => {
    let active = true;
    let removeNativeListener = () => {};
    let removeEventListener = () => {};

    // Device Context is Android-native only. The PWA must remain a true no-op:
    // no feature API reads, preference inserts, local preference restoration
    // or native bridge probes are performed from this provider.
    if (!ownerId || !isAthenaDeviceContextAvailable()) {
      setPreferencesState(ATHENA_DEVICE_CONTEXT_DEFAULTS);
      setSnapshot(emptySnapshot());
      setRuntimeCapabilities(null);
      return undefined;
    }

    const bootstrap = async () => {
      const local = readLocalPreferences(ownerId);
      const [binding, nativeCapabilities, runtime] = await Promise.all([
        bindAthenaDeviceContextOwner(ownerId),
        getAthenaDeviceContextCapabilities(),
        getAthenaRuntimeCapabilities(),
      ]);
      if (!active) return;
      // A native installation can survive logout. Do not let a cached local
      // preference from a prior account re-enable observation while this new
      // owner is being bound.
      const effectiveLocal = binding?.ownerChanged
        ? ATHENA_DEVICE_CONTEXT_DEFAULTS
        : local;
      if (binding?.ownerChanged) {
        // A new account must never inherit device-context consent from the
        // previous account, even on the same physical phone.
        writeLocalPreferences(ownerId, ATHENA_DEVICE_CONTEXT_DEFAULTS);
        setPreferencesState(ATHENA_DEVICE_CONTEXT_DEFAULTS);
      }
      preferencesRef.current = effectiveLocal;
      setPreferencesState(effectiveLocal);
      setCapabilities(nativeCapabilities);
      setRuntimeCapabilities(runtime);
      setSnapshot(emptySnapshot(nativeCapabilities));

      // Remote preferences can restore category choices but never elevate
      // `enabled` on a new device. The explicit local device choice is the
      // only path that starts observation.
      const token = typeof localStorage !== 'undefined' ? localStorage.getItem('athena_token') : null;
      const remote = await authenticatedFetch('/device-context/preferences', token);
      if (!active || !remote) return;
      const remotePreferences = normalizePreferences(remote.preferences || remote);
      const merged = normalizePreferences({
        ...remotePreferences,
        enabled: effectiveLocal.enabled === true,
        appState: effectiveLocal.appState === true,
      });
      writeLocalPreferences(ownerId, merged);
      preferencesRef.current = merged;
      setPreferencesState(merged);
    };

    void bootstrap();

    removeEventListener = subscribeAthenaEvents((event) => {
      if (!active || !deviceContextReleased || !preferencesRef.current.enabled) return;
      if (event.type === ATHENA_EVENT_TYPES.APP_FOREGROUND || event.type === ATHENA_EVENT_TYPES.APP_BACKGROUND) {
        if (!preferencesRef.current.appState) return;
        setSnapshot((previous) => ({ ...previous, appState: event.type === ATHENA_EVENT_TYPES.APP_FOREGROUND ? 'foreground' : 'background', collectedAt: event.timestamp }));
      }
      if (event.type === ATHENA_EVENT_TYPES.NETWORK_CHANGED && preferencesRef.current.network) {
        setSnapshot((previous) => ({ ...previous, network: { connected: event.metadata.connected !== false, type: event.metadata.connectionType || 'unknown' }, collectedAt: event.timestamp }));
      }
    });

    return () => {
      active = false;
      try { removeNativeListener?.(); } catch { /* noop */ }
      try { removeEventListener?.(); } catch { /* noop */ }
    };
  }, [deviceContextReleased, ownerId]);

  useEffect(() => {
    let active = true;
    let removeNativeListener = () => {};
    if (!isAthenaDeviceContextAvailable()) return undefined;
    if (!ownerId || !preferences.enabled || !deviceContextReleased || !hasEnabledSignal(preferences)) {
      void setAthenaDeviceContextEnabledSignals(ATHENA_DEVICE_CONTEXT_DEFAULTS);
      if (preferences.enabled && (!deviceContextReleased || !hasEnabledSignal(preferences))) {
        void setAthenaDeviceContextConsent(false);
        void stopAthenaDeviceContextObserving();
      }
      return undefined;
    }

    const observe = async () => {
      // Configure the native filter first. This makes the explicit category
      // toggles a collection boundary rather than merely a send-time filter.
      await setAthenaDeviceContextEnabledSignals(nativeEnabledSignals(preferences));
      await setAthenaDeviceContextConsent(true);
      await startAthenaDeviceContextObserving();
      const rawSnapshot = await getAthenaDeviceContextSnapshot();
      const next = maskSnapshotForPreferences(rawSnapshot, preferences, capabilities);
      if (active) setSnapshot(next);
      removeNativeListener = await observeAthenaDeviceContext((raw) => {
        if (!active) return;
        const event = normalizeAthenaDeviceContextEvent(raw);
        const maskedSnapshot = maskSnapshotForPreferences(event.snapshot, preferencesRef.current, capabilities);
        setSnapshot(maskedSnapshot);
        emitAthenaEvent({
          type: ATHENA_EVENT_TYPES.DEVICE_CONTEXT_CHANGED,
          source: ATHENA_EVENT_SOURCES.DEVICE,
          metadata: { signal: event.signal, available: true },
          idempotencyKey: `device-context:${event.signal}:${maskedSnapshot.collectedAt || 'unknown'}`,
        });
      });
    };
    void observe();
    return () => {
      active = false;
      try { removeNativeListener?.(); } catch { /* noop */ }
      void stopAthenaDeviceContextObserving();
    };
  }, [capabilities, deviceContextReleased, ownerId, preferences]);

  const setPreferences = useCallback(async (nextPreferences) => {
    if (!ownerRef.current) return { ok: false, reason: 'authentication_required' };
    if (!isAthenaDeviceContextAvailable()) return { ok: false, reason: 'native_device_context_unavailable' };
    const previous = preferencesRef.current;
    const next = normalizePreferences({ ...previous, ...nextPreferences });
    if (next.enabled && !deviceContextReleased) {
      return { ok: false, reason: 'device_context_not_released' };
    }
    writeLocalPreferences(ownerRef.current, next);
    // Apply the native restriction before consent/observation can begin. A
    // stale or old native bridge safely no-ops; the React mask remains active.
    const nativeSignals = await setAthenaDeviceContextEnabledSignals(nativeEnabledSignals(next));
    setPreferencesState(next);
    const nativeConsent = await setAthenaDeviceContextConsent(hasEnabledSignal(next));
    if (!next.enabled) {
      await stopAthenaDeviceContextObserving();
      setSnapshot(emptySnapshot(capabilities));
    }

    // This is an explicit settings change, never an app-boot write.
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('athena_token') : null;
    const installation = await getAthenaNativeInstallation();
    // The Worker refuses a bare authenticated write: this transient proof
    // binds the explicit Android setting change to the current installation.
    // It is never persisted in local preferences, events or diagnostics.
    if (installation) {
      void authenticatedFetch('/device-context/preferences', token, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...workerPreferences(next),
          installation_id: installation.installationId,
          installation_proof: installation.installationProof,
        }),
      });
    }
    return { ok: true, preferences: next, nativeConsent, nativeSignals };
  }, [capabilities, deviceContextReleased]);

  const selectRelevantContext = useCallback((requestedSignals) => (
    selectRelevantAthenaDeviceContext(snapshot, preferences, requestedSignals)
  ), [preferences, snapshot]);

  const syncRelevantContext = useCallback(async (requestedSignals, { fresh = false } = {}) => {
    if (!isAthenaDeviceContextAvailable()) return { ok: false, reason: 'native_device_context_unavailable' };
    if (!deviceContextReleased) return { ok: false, reason: 'device_context_not_released' };
    if (!preferences.enabled || !hasRequestedEnabledSignal(preferences, requestedSignals)) {
      return { ok: false, reason: 'context_not_enabled_or_relevant' };
    }
    // A fresh read is permitted only from a direct, explicit user action
    // (currently a clearly device-related chat question). It is never run
    // while mounting a screen or for ordinary messages.
    const rawSnapshot = fresh ? await getAthenaDeviceContextSnapshot() : snapshot;
    const sourceSnapshot = maskSnapshotForPreferences(rawSnapshot, preferences, capabilities);
    const context = selectRelevantAthenaDeviceContext(sourceSnapshot, preferences, requestedSignals);
    if (!Object.keys(context).length) return { ok: false, reason: 'context_not_enabled_or_relevant' };
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('athena_token') : null;
    const installation = await getAthenaNativeInstallation();
    if (!installation) return { ok: false, reason: 'native_registration_unavailable' };
    const reducedSnapshot = workerSnapshot(context);
    if (!Object.keys(reducedSnapshot).length) return { ok: false, reason: 'context_not_supported_by_worker' };
    const result = await authenticatedFetch('/devices/context', token, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        installation_id: installation.installationId,
        installation_proof: installation.installationProof,
        idempotency_key: contextIdempotencyKey(reducedSnapshot, context.collectedAt),
        snapshot: reducedSnapshot,
      }),
    });
    return result
      ? { ok: true, result, installation }
      : { ok: false, reason: 'context_sync_unavailable' };
  }, [capabilities, deviceContextReleased, preferences, snapshot]);

  const publishRelevantContext = useCallback(async (requestedSignals) => {
    const published = await syncRelevantContext(requestedSignals);
    // The registration proof stays transient and private to the trusted
    // message path below. It is never part of the public generic publish API.
    return published.ok
      ? { ok: true, result: published.result }
      : published;
  }, [syncRelevantContext]);

  const prepareChatContext = useCallback(async (message) => {
    const requestedSignals = requestedAthenaDeviceSignalsForMessage(message);
    if (!requestedSignals.length || !deviceContextReleased || !preferences.enabled) return null;

    const published = await syncRelevantContext(requestedSignals, { fresh: true });
    if (!published.ok || !published.installation) return null;

    // This is deliberately just a proof reference. The chat request never
    // contains a raw snapshot, location, audio, content, or diagnostics. The
    // Worker decides whether the previously stored, relevant snapshot is safe
    // to use for this same explicit message.
    return Object.freeze({
      installation_id: published.installation.installationId,
      installation_proof: published.installation.installationProof,
    });
  }, [deviceContextReleased, preferences.enabled, syncRelevantContext]);

  const value = useMemo(() => ({
    available: isAthenaDeviceContextAvailable(),
    released: deviceContextReleased,
    ownerId: ownerId || null,
    preferences,
    snapshot,
    capabilities,
    runtimeCapabilities,
    refreshSnapshot,
    setPreferences,
    selectRelevantContext,
    publishRelevantContext,
    prepareChatContext,
  }), [capabilities, deviceContextReleased, ownerId, preferences, prepareChatContext, publishRelevantContext, refreshSnapshot, runtimeCapabilities, selectRelevantContext, setPreferences, snapshot]);

  return <AthenaDeviceContext.Provider value={value}>{children}</AthenaDeviceContext.Provider>;
}

export function useAthenaDeviceContext() {
  const context = useContext(AthenaDeviceContext);
  if (!context) throw new Error('useAthenaDeviceContext must be used inside AthenaDeviceContextProvider');
  return context;
}
